import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { AppLabel } from "../../component/AppLabel";
import { AppInput } from "../../component/AppInput";

import {
  AppCheckBox,
  AppInputGroup,
  AppSelect,
  AppTextArea,
  SelectOption,
} from "@dcam/dcam_flhealth_component_library";
import { AppButton } from "../../component/AppButton";
import ApiClient from "../../client/ApiClient";
import useGetData from "../../hooks/useGetData";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import usePost from "../../hooks/usePost";
import { ApiResponse } from "../Dashboard/ApiResponse";
import {
  CreateUserFormValidationSchema,
  RoleData,
  UserDataFormRequest,
} from "./CreateUser.types";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import Loader from "../../component/Loader/Loader";
import { AppCard } from "../../component/AppCard";
import { uploadFile } from "../../hooks/uploadFile";
import { UserDetails } from "../Dashboard/Dashboard.types";

const mapToSelectOptions = (data: RoleData[]): SelectOption[] => {
  return data.map((item) => ({
    value: item.id,
    label: item.value,
  }));
};

type SaveUserResponse = {
  userId?: number | string;
  UserId?: number | string;
  data?: unknown;
  result?: unknown;
};

const getSavedUserId = (response: unknown): number | null => {
  if (typeof response === "number") {
    return Number.isInteger(response) && response > 0 ? response : null;
  }

  if (typeof response === "string") {
    const numericId = Number(response);
    if (Number.isInteger(numericId) && numericId > 0) {
      return numericId;
    }

    try {
      return getSavedUserId(JSON.parse(response));
    } catch {
      return null;
    }
  }

  if (response && typeof response === "object") {
    const value = response as SaveUserResponse;
    const directId = value.userId ?? value.UserId;

    if (directId !== undefined) {
      return getSavedUserId(directId);
    }

    // Support hooks that return an Axios response or wrap the API result.
    return getSavedUserId(value.data ?? value.result);
  }

  return null;
};

const CreateUser = () => {
  const isAuthenticated = useIsAuthenticated();

  const { instance, accounts } = useMsal();

  const account = accounts[0];
  const loggedInUser = accounts[0] && accounts[0].username;

  const methods = useForm({
    resolver: yupResolver(CreateUserFormValidationSchema),
    defaultValues: {
      userIsActive: true,
      createdBy: loggedInUser,
    },
  });

  const [fieldsEnabled, setFieldsEnabled] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailErrorColor, setEmailErrorColor] = useState("");
  const [userDataAgreementFile, setUserDataAgreementFile] = useState<File | null>(null);
  const [ssnPolicyFile, setSsnPolicyFile] = useState<File | null>(null);

  const [routinizedHospitalEnabled, setRoutinizedHospitalEnabled] =
    useState(false);
  const [routinizedFacilityEnabled, setRoutinizedFacilityEnabled] =
    useState(false);

  const { getDataById: checkEmailExist } = useGetData(
    `UserSecurity/CheckUserByEmail`,
    "GET"
  );

  const [allRolesData, setAllRolesData] = useState<SelectOption[]>([]);
  const { getDataById: getRoles } = useGetData<RoleData[], string>(
    "Client/Roles",
    "GET"
  );

  const { getDataById: getUserId } = useGetData<any, string>(
    "UserSecurity/GetUserSecurityByEmail/" + account.username,
    "GET"
  );

  const [userDetails, setUserDetails] = useState<UserDetails>();

  const hasFetched = useRef(false); // Declare outside useEffect

  const [roleId, setRole] = useState(0);


  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getUserId("");
        if (userData?.user && userData?.user?.isActive) {
          setUserDetails({ ...userData.user }); // Update state
          setRole(userData?.user?.roleId)
        } else {
          navigate('/unauthorized');
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    if (isAuthenticated && !hasFetched.current) {
      hasFetched.current = true; // Mark as fetched
      fetchUserData();
    }
  }, [isAuthenticated, getUserId]); // Dependency array

  useEffect(() => {
    let isMounted = true;

    const fetchRoles = async () => {
      try {
        const data = await getRoles("");
        if (data && isMounted) {
          const mappedRoles = mapToSelectOptions(data);
          setAllRolesData([
            { value: "0", label: "----- Select Role -----" },
            ...mappedRoles,
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch roles", error);
      }
    };

    fetchRoles();

    const fetchRoutinizedHospitals = async () => {
      try {
        debugger;
        var data: RoleData[] = await getRoutinizedHospitals("");
        if (data && isMounted) {
          if (
  [
    "Provider",
    "Staff",
    "HIV Provider",
    "STD Provider",
    "Program Admin",
    "HIV Program Admin",
    "STD Program Admin",
  ].includes(userDetails?.roleName ?? "")
) {
  data = data.filter(
    (hospital) => hospital.id === userDetails?.routinizedHospitalId
  );

  // Auto-select the assigned hospital
  if (data.length === 1) {
    methods.setValue("routinizedHospital", String(data[0].id));
  }
}
          const mappedRoles = mapToSelectOptions(data);
          setallRoutinizedHospitalsData([
            { value: "0", label: "----- Select Routinized Hospitals -----" },
            ...mappedRoles,
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch roles", error);
      }
    };

    fetchRoutinizedHospitals();

    return () => {
      isMounted = false;
    };
  }, [userDetails]);

  const [allRoutinizedHospitalsData, setallRoutinizedHospitalsData] = useState<
    SelectOption[]
  >([]);
  const { getDataById: getRoutinizedHospitals } = useGetData<
    RoleData[],
    string
  >("Client/RoutinizedHospitals", "GET");

  const navigate = useNavigate();

  const handleUserDataAgreementFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUserDataAgreementFile(e.target.files[0]);
    }
  };

  const handleSsnPolicyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSsnPolicyFile(e.target.files[0]);
    }
  };

  const handleValidateEmail = async () => {
    const isValid = await methods.trigger("userEmail");
    if (!isValid) return;

    const email = methods.getValues("userEmail");

    try {
      const data = await checkEmailExist(email);

      if (data === true) {
        setEmailError(
          "Email already exists cannot be used, try another email."
        );
        toast.error("Email already exists cannot be used, try another email.", {
          position: "top-right",
          autoClose: 2000,
        });
        setEmailErrorColor("red");
        setFieldsEnabled(false);
      } else if (data === false) {
        setEmailError("Email Address can be used!");
        toast.success("Email Address can be used!", {
          position: "top-right",
          autoClose: 2000,
        });
        setEmailErrorColor("green");
        setFieldsEnabled(true);
      } else {
        setFieldsEnabled(false);
      }
    } catch (error) {
      console.error("API call failed", error);
    }
  };

  const { postByObject: postUser } = usePost<SaveUserResponse, UserDataFormRequest>(
    "UserSecurity/Update",
    "POST"
  );

  const [isLoader, setisLoader] = useState<boolean>(false);

  const handleFileUpload = async (file: File, userId: number) => {
    // Allowed file extensions
    const allowedExtensions = [".pdf", ".jpg", ".png", ".docx", ".gif"];
    const fileExtension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      console.error(`Invalid file type: ${fileExtension}`);
      throw new Error(`File type not allowed. Allowed types: ${allowedExtensions.join(", ")}`);
    }

    const token = sessionStorage.getItem("accessToken");
    const fileUpload = {
      fileName: file.name,
      documentName: file.name,
      filePath: "",
      createdBy: "Admin",
    };

    try {
      const response = await uploadFile(file, userId, fileUpload, token);
      console.log("File uploaded successfully:", response);
      return response;
    } catch (error: any) {
      console.error("File upload failed:", error.message || error);

      // Throw the error to be caught in the calling function
      throw new Error(error.message || "File upload failed.");
    }
  };


  // const handleCreateUserSubmit = async () => {
  //   const userFormData = methods.getValues();
  //   console.log(userFormData);

  //   // if (!userDataAgreementFile || !ssnPolicyFile) {
  //   //   toast.error("Please upload both required files before submitting.", {
  //   //     position: "top-right",
  //   //     autoClose: 2000,
  //   //   });
  //   //   return;
  //   // }

  //   const userConfirmed = window.confirm(
  //     "Please confirm if you would like to save?"
  //   );
  //   if (!userConfirmed) {
  //     return;
  //   }

  //   try {
  //     setisLoader(true);
  //     const saveResponse = await postUser(userFormData);
  //     const userId = saveResponse;

  //     if (!userId) {
  //       throw new Error("Failed to create user. No userId returned.");
  //     }

  //     if (userDataAgreementFile) {
  //       await handleFileUpload(userDataAgreementFile, userId);
  //     }

  //     if (ssnPolicyFile) {
  //       await handleFileUpload(ssnPolicyFile, userId);
  //     }
  //     //console.log(saveResponse, 'heree');

  //     toast.success("Data saved successfully!", {
  //       position: "top-right",
  //       autoClose: 2000,
  //     });

  //     setTimeout(() => {
  //       setisLoader(false);
  //       navigate("/dashboard");
  //     }, 2000);
  //   } catch (error) {
  //     setisLoader(false);
  //     toast.error("Failed to save user!", {
  //       position: "top-right",
  //       autoClose: 2000,
  //     });
  //     console.error(error);
  //   }
  // };

  const handleCreateUserSubmit = async () => {
    const userFormData = methods.getValues();

    // Validate if both files are uploaded
    if (!userDataAgreementFile || !ssnPolicyFile) {
      toast.error("Please upload both required files before submitting.", {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }

    // Confirm user action
    const userConfirmed = window.confirm(
      "Please confirm if you would like to save?"
    );
    if (!userConfirmed) {
      return;
    }

    try {
      setisLoader(true);

      // Step 1: Validate and upload files before creating the user
      const fileValidationAndUpload = async (file: File, userId: number) => {
        // Ensure the file exists before processing
        if (!file) {
          throw new Error("File not provided.");
        }

        try {
          return await handleFileUpload(file, userId);
        } catch (error) {
          if (error instanceof Error) {
            throw new Error(`${file.name}: ${error.message}`);
          } else {
            throw new Error(` ${file.name}: Unknown error.`);
          }
        }
      };

      // Temporary userId to validate files first
      const temporaryUserId = -1;

      // Validate and upload files
      await fileValidationAndUpload(userDataAgreementFile, temporaryUserId);
      await fileValidationAndUpload(ssnPolicyFile, temporaryUserId);

      // Step 2: Create the user after successful file uploads
      const saveResponse = await postUser(userFormData);
      const userId = getSavedUserId(saveResponse);

      if (userId === null) {
        console.error("Unexpected create-user response:", saveResponse);
        throw new Error("Failed to create user. No valid userId returned.");
      }

      // Step 3: Re-associate uploaded files with the created user
      const associateFilesWithUser = async (file: File) => {
        try {
          await handleFileUpload(file, userId);
        } catch (error) {
          if (error instanceof Error) {
            console.error(`Failed to associate file ${file.name} with user: ${error.message}`);
          } else {
            console.error(`Failed to associate file ${file.name} with user: Unknown error.`);
          }
        }
      };

      await associateFilesWithUser(userDataAgreementFile);
      await associateFilesWithUser(ssnPolicyFile);

      toast.success("Data saved successfully!", {
        position: "top-right",
        autoClose: 2000,
      });

      setTimeout(() => {
        navigate("/user/Dashboard");
        setisLoader(false);
      }, 2000);
    } catch (error) {
      setisLoader(false);

      if (error instanceof Error) {
        toast.error(`${error.message}`, {
          position: "top-right",
          autoClose: 15000,
        });
      } else {
        toast.error("An unexpected error occurred.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
      console.error("Error occurred:", error);
    }
  };


  const handleEmailChange = () => {
    methods.reset({
      userEmail: methods.getValues("userEmail"),
      userAddress: "",
      userCity: "",
      userFirstName: "",
      userLastName: "",
      userPhone: "",
      userRole: "0",
      userZipcode: "",
      userNotes: "",
      routinizedFacility: "",
      routinizedHospital: "0",
    });
    setEmailError("");
    setFieldsEnabled(false);
    setRoutinizedFacilityEnabled(false);
    setRoutinizedHospitalEnabled(false);
  };

  const handleClearButton = () => {
    methods.reset({
      userEmail: "",
      userAddress: "",
      userCity: "",
      userFirstName: "",
      userLastName: "",
      userPhone: "",
      userRole: "0",
      userZipcode: "",
      userNotes: "",
      routinizedFacility: "",
      routinizedHospital: "0",
    });
    setEmailError("");
    setFieldsEnabled(false);
    setRoutinizedFacilityEnabled(false);
    setRoutinizedHospitalEnabled(false);
  };

  const [filteredRoutinizedFacilities, setFilteredRoutinizedFacilities] =
    useState<any[]>([]);

  const { getDataById: getRoutinizedFacilities } = useGetData<
    RoleData[],
    string
  >("Client/RoutinizedFacilities", "GET");

  const handleRoutinizedHospitalChange = async () => {
    const selectedHospital = methods.getValues("routinizedHospital");

    if (selectedHospital == "0") {
      setFilteredRoutinizedFacilities([]);
      setRoutinizedFacilityEnabled(false);
    } else {
      try {
        const facilityData = await getRoutinizedFacilities(
          `${selectedHospital}`
        );

        if (facilityData) {
          const mappedFacilities = mapToSelectOptions(facilityData);
          setFilteredRoutinizedFacilities([
            { value: "0", label: "----- Select Routinized Facilities -----" },
            ...mappedFacilities,
          ]);
        }

        setRoutinizedFacilityEnabled(true);
      } catch (error) {
        setFilteredRoutinizedFacilities([]);
        setRoutinizedFacilityEnabled(false);
      }
    }
  };

  const handleRoleChange = () => {
    const role = methods.getValues("userRole");

    if (role == "0" || role == "1") {
      setRoutinizedHospitalEnabled(false);
      setRoutinizedFacilityEnabled(false);
      setFilteredRoutinizedFacilities([]);
    } else {
      setRoutinizedHospitalEnabled(true);
    }
  };


  const getFilteredRoles = (allRoles: SelectOption[]): SelectOption[] => {
    if (roleId === 4) {
      const allowedRoles = allRolesData.filter(role =>
        [4, 5].includes(role.value as number)
      );
      return [{ value: "0", label: "----- Select Role -----" }, ...allowedRoles];
    }
    if (roleId === 6) {
      const allowedRoles = allRolesData.filter(role =>
        [6, 7].includes(role.value as number)
      );
      return [{ value: "0", label: "----- Select Role -----" }, ...allowedRoles];
    }
    if (roleId === 2) {
      const allowedRoles = allRolesData.filter(role =>
        [2, 3, 4, 5, 6, 7].includes(role.value as number)
      );
      return [{ value: "0", label: "----- Select Role -----" }, ...allowedRoles];
    }
    if (roleId === 8) {
  const allowedRoles = allRolesData.filter(role =>
    [4, 5, 6, 7].includes(role.value as number)
  );

  return [
    { value: "0", label: "----- Select Role -----" },
    ...allowedRoles
  ];
}
if (roleId === 9) {
  const allowedRoles = allRolesData.filter(role =>
    [4, 5].includes(role.value as number)
  );

  return [
    { value: "0", label: "----- Select Role -----" },
    ...allowedRoles
  ];
}
if (roleId === 10) {
  const allowedRoles = allRolesData.filter(role =>
    [6, 7].includes(role.value as number)
  );

  return [
    { value: "0", label: "----- Select Role -----" },
    ...allowedRoles
  ];
}

    return allRolesData;
  };

  return (
    <>
      {isLoader ? (
        <div>
          <Loader />
        </div>
      ) : (
        <Container>
          <Row className="mb-3">
            <Col>
          <AppCard title="User Information" topMargin>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(handleCreateUserSubmit)}>
                <div className="row">
                  <div className="col-md-12 col-lg-6">
                    <AppLabel label="Email" required></AppLabel>
                    <AppInputGroup
                      type="text"
                      name="userEmail"
                      isIconButton
                      actionText="Check Email"
                      required
                      onClick={handleValidateEmail}
                      onCustomChange={handleEmailChange}
                    ></AppInputGroup>
                    {emailError && (
                      <span style={{ color: emailErrorColor }}>{emailError}</span>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 col-lg-6">
                    <AppLabel label="First Name" required></AppLabel>
                    <AppInput name="userFirstName" disabled={!fieldsEnabled} />
                  </div>
                  <div className="col-md-12 col-lg-6">
                    <AppLabel label="Last Name" required></AppLabel>
                    <AppInput name="userLastName" disabled={!fieldsEnabled} />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 col-lg-6">
                    <AppLabel label="Address"></AppLabel>
                    <AppInput name="userAddress" disabled={!fieldsEnabled} />
                  </div>
                  <div className="col-md-12 col-lg-6">
                    <AppLabel label="City"></AppLabel>
                    <AppInput name="userCity" disabled={!fieldsEnabled} />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 col-lg-6">
                    <AppLabel label="Zip Code"></AppLabel>
                    <AppInput name="userZipcode" disabled={!fieldsEnabled} />
                  </div>
                  <div className="col-md-12 col-lg-6">
                    <AppLabel label="Work Phone"></AppLabel>
                    <AppInput name="userPhone" disabled={!fieldsEnabled} />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 col-lg-6">
                    <AppLabel label="Select Role" required></AppLabel>
                    <AppSelect
                      name="userRole"
                      //options={allRolesData}
                      options={roleId !== 1 ? getFilteredRoles(allRolesData) : allRolesData}
                      disabled={!fieldsEnabled}
                      onCustomChange={handleRoleChange}
                    />
                  </div>
                  <div className="col-md-12 col-lg-6">
                    <div className="pt-5"></div>
                    <AppCheckBox name="userIsActive" label="Active" />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 col-lg-6">
                    <AppLabel
                      label="Routinized Testing Focus Project - Hospital"
                      required
                    ></AppLabel>
                    <AppSelect
                      name="routinizedHospital"
                      options={allRoutinizedHospitalsData}
                      disabled={!routinizedHospitalEnabled}
                      onCustomChange={handleRoutinizedHospitalChange}
                    />
                  </div>
                  <div className="col-md-12 col-lg-6">
                    <AppLabel label="Routinized Testing Focus Project - Facility"></AppLabel>
                    <AppSelect
                      name="routinizedFacility"
                      options={filteredRoutinizedFacilities}
                      disabled={!routinizedFacilityEnabled}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <AppLabel label="Notes"></AppLabel>
                    <AppTextArea
                      name="userNotes"
                      rows={5}
                      disabled={!fieldsEnabled}
                    />
                  </div>
                  <div className="col-md-6">
                    <div className="row">
                      <div className="col-12">
                        <AppLabel label="User Data Agreement Document" required></AppLabel>
                        <input
                          type="file"
                          name="userDataAgreementFile"
                          disabled={!fieldsEnabled}
                          onChange={handleUserDataAgreementFileChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-12 mt-3">
                        <AppLabel label="SSN Policy File" required></AppLabel>
                        <input
                          type="file"
                          name="ssnPolicyFile"
                          disabled={!fieldsEnabled}
                          onChange={handleSsnPolicyFileChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>
                </div>


                <div className="row my-4 justify-content-end">
                  <div className="container d-flex justify-content-end align-items-center flex-wrap flex-column flex-lg-row">
                    <div className="text-end">
                      <AppButton
                        type="submit"
                        variant="primary"
                        disabled={!fieldsEnabled}
                      >
                        Submit
                      </AppButton>
                      &nbsp;
                      <AppButton
                        type="button"
                        variant="secondary"
                        disabled={!fieldsEnabled}
                        onClick={handleClearButton}
                      >
                        Clear
                      </AppButton>
                    </div>
                  </div>
                </div>
                <input type="hidden" {...methods.register("createdBy")} />
              </form>
            </FormProvider>
          </AppCard>
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
};

export default CreateUser;
